import { useNavigate } from "react-router-dom";
import {
  Wrench,
  LogOut,
  ExternalLink,
} from "lucide-react";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { useAuth } from "../hooks/useAuth";
import { showConfirm } from "../utils/confirm";
import { ROUTES } from "../config/constants";

export default function Portal() {
  const navigate = useNavigate();
  const { userName } = useCurrentUser();
  const { forceLogout } = useAuth();

  function handleLogout() {
    showConfirm({
      title: "Konfirmasi Logout",
      message: "Apakah Anda yakin ingin keluar dari aplikasi?",
      confirmLabel: "Ya, Logout",
      cancelLabel: "Batal",
      onConfirm: () => {
        forceLogout();
        navigate(ROUTES.LOGIN);
      },
    });
  }

  const cards = [
    {
      title: "OPIT",
      desc:
        "Aplikasi untuk optimasi cutting plan rebar, perhitungan emisi karbon, dan management potongannya.",
      icon: <Wrench size={34} className="text-orange-500" />,
      glow: "from-orange-500/10 to-amber-400/5",
      border: "border-orange-100",
      bgIcon: "bg-orange-50",
      link: "https://opit.strategic.web.id",
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-white px-6 py-8 md:px-10">
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-120px] left-[-80px] w-[380px] h-[380px] bg-orange-200/40 rounded-full blur-3xl animate-pulse" />

        <div
          className="absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] bg-amber-200/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "6s" }}
        />

        <div
          className="absolute top-[40%] left-[45%] w-[260px] h-[260px] bg-yellow-200/30 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "8s" }}
        />

        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white/70 backdrop-blur-md mb-4 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />

              <span className="text-[11px] tracking-wider text-gray-600 uppercase">
                Engineering Utility Platform
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight">
              Engineering Tools
            </h1>

            <p className="text-gray-500 mt-3 text-sm">
              Selamat datang kembali,{" "}
              <span className="text-gray-900 font-medium">
                {userName}
              </span>
            </p>
          </div>

          <button
            onClick={handleLogout}
            className="group flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 bg-white/80 border border-red-100 backdrop-blur-xl rounded-2xl hover:bg-red-50 transition-all duration-300 shadow-sm"
          >
            <LogOut
              size={16}
              className="group-hover:rotate-6 transition-transform"
            />

            <span>Logout</span>
          </button>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((item, index) => (
            <a
              key={index}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`group relative overflow-hidden rounded-3xl border ${item.border} bg-white/70 backdrop-blur-xl p-7 min-h-[300px] transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl shadow-lg`}
            >
              {/* Hover Glow */}
              <div
                className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 bg-gradient-to-br ${item.glow}`}
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-white/40" />

              <div className="relative z-10 flex flex-col h-full">
                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl ${item.bgIcon} border border-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-sm`}
                >
                  {item.icon}
                </div>

                {/* Title */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {item.title}
                  </h2>

                  <ExternalLink
                    size={15}
                    className="text-gray-400 group-hover:text-gray-700 transition"
                  />
                </div>

                {/* Description */}
                <p className="text-sm text-gray-500 leading-relaxed">
                  {item.desc}
                </p>

                {/* Footer */}
                <div className="mt-auto pt-8">
                  <div className="inline-flex items-center gap-2 text-xs text-gray-600 group-hover:text-gray-900 transition">
                    <span>Open Application</span>

                    <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                      →
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Hover */}
              <div className="absolute inset-0 rounded-3xl border border-white/40 group-hover:border-gray-200 transition-all duration-500" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
